import { z } from 'zod';

/**
 * Roster payload: expert id to whether the badge shows for them. Keys arrive as strings
 * because JSON object keys always do, and because directory profiles carry ids as strings.
 *
 * A switch rather than a threshold. Qualification is decided offline, against a window and
 * a set of reference channels the client cannot see, so a rate in the payload could only
 * ever be re-checked against a bar the client had no way to verify the number met. It was
 * also never rendered. Deciding eligibility where the data actually lives, and shipping the
 * answer rather than the working, is the honest split.
 *
 * A number is still accepted and still means on. Every environment and segment override
 * currently holds a rate-shaped roster, so rejecting numbers would blank every badge the
 * moment this deploys, and the number is worth keeping as a note of why an expert
 * qualified. Only an explicit `false` turns an entry off.
 */
const rosterEntrySchema = z.union([z.boolean(), z.number()]);

export const referenceChampionRosterSchema = z.record(z.string(), rosterEntrySchema);

/**
 * A parsed roster, normalized to plain on/off so downstream never has to ask what a value
 * meant. Entries that parsed to off are kept rather than dropped: the wildcard is looked up
 * when a key is missing, so dropping them would let it silently switch back on the very
 * expert an operator turned off by hand.
 */
export type ReferenceChampionRoster = Record<string, boolean>;

/**
 * Fallback name, used when the payload does not carry one. The badge has to say something
 * to exist at all, so this is the one piece of copy code still has an opinion about.
 *
 * It carries no rate, and neither does the accessible name. A bare percent does not say
 * what it is a share of, and the honest figure depends on the window: the same expert reads
 * 49 percent over their whole history, 70 over their last ten inquiries and 80 over their
 * last twenty. Qualification is settled offline; the payload carries the answer.
 */
export const REFERENCE_CHAMPION_DEFAULT_LABEL = 'Reference Champion';

/**
 * Copy is operator-editable for the same reason the roster is. The name and the mark are
 * the parts most likely to be retuned after the badge meets real experts, and both are
 * pure presentation, so requiring a deploy to change a word would make this the one part
 * of the feature that cannot move at the speed of the decision behind it.
 *
 * Both copy fields are optional, so an operator can set a roster without restating copy
 * they do not want to change. They differ in what absence means: a missing name falls back,
 * because a badge with nothing to say cannot render, while a missing mark simply means no
 * mark. Code holds no default glyph, so omitting the key is how an operator turns the mark
 * off, and no environment inherits a design decision it never asked for.
 */
const copyFieldSchema = z.string().trim().min(1);

/**
 * The keys that mark a payload as the copy shape rather than a bare roster.
 *
 * Presence of any one of them is the discriminator. It has to be presence rather than a
 * schema match, because a bare roster is also a plain object of string keys and would
 * otherwise satisfy an all-optional copy schema with every field undefined, parsing to an
 * empty roster and hiding every badge.
 */
const COPY_KEYS = ['label', 'emoji', 'roster'] as const;

export type ReferenceChampionConfig = {
  roster: ReferenceChampionRoster;
  label: string;
  /** Absent when the payload sets no mark, which renders the label on its own. */
  emoji?: string;
  /**
   * Human-readable notes about anything the payload asked for that could not be honored.
   *
   * Parsing never throws and never returns nothing on account of one bad part, so without
   * this a typo would be indistinguishable from a deliberate edit. Callers report these so
   * an operator finds out from an alert rather than from the badge quietly not appearing.
   */
  issues: string[];
};

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * Roster key that badges every expert not listed explicitly.
 *
 * Exists because expert ids are not stable across environments. A roster curated
 * against production ids badges nobody in dev at best, and at worst badges a
 * different person who happens to occupy that id, so a demo or QA pass needs a
 * way to light the badge up without first discovering which experts are indexed
 * in that environment.
 *
 * Only ever belongs in a non-production roster. In production it would badge every expert,
 * including those who have never published a reference.
 */
export const REFERENCE_CHAMPION_WILDCARD = '*';

/**
 * Normalizes a feature flag payload into something safe to render.
 *
 * For the MVP, Flagsmith is the only runtime source and there is no roster in code. Two
 * payload shapes are accepted, a bare roster and a roster with copy alongside it, because
 * the bare shape is what the environments already hold and rejecting it would blank the
 * badge in all of them.
 *
 * Every part is validated on its own and a bad part costs only itself: one unreadable entry
 * drops that expert, an unusable label falls back to the default, and the rest of the
 * payload still renders. The whole payload used to be validated as a unit, so a single
 * mistyped value, a stray key, or an empty string took the entire roster down with it and
 * every badge disappeared at once. That is the worst available outcome for a config an
 * operator hand-edits, and it gave no signal that anything was wrong.
 */
export function parseReferenceChampionConfig(value: unknown): ReferenceChampionConfig {
  const issues: string[] = [];

  if (!isPlainObject(value)) {
    if (value !== undefined && value !== null) {
      issues.push('payload is not an object, so no badges resolve');
    }

    return { roster: {}, label: REFERENCE_CHAMPION_DEFAULT_LABEL, issues };
  }

  const isCopyShape = COPY_KEYS.some((key) => key in value);

  /**
   * Returns the payload's copy for a key, or undefined when it sets none. An unusable value
   * is reported and then treated as absent, so a typo lands on whatever absence means for
   * that key rather than rendering the operator's mistake.
   */
  const readCopy = (key: 'label' | 'emoji'): string | undefined => {
    const raw = isCopyShape ? value[key] : undefined;

    if (raw === undefined) {
      return undefined;
    }

    const parsed = copyFieldSchema.safeParse(raw);

    if (!parsed.success) {
      issues.push(`${key} is not a non-empty string, so it is treated as unset`);

      return undefined;
    }

    return parsed.data;
  };

  const label = readCopy('label') ?? REFERENCE_CHAMPION_DEFAULT_LABEL;

  const emoji = readCopy('emoji');

  const rawRoster = isCopyShape ? value.roster : value;

  const roster: ReferenceChampionRoster = {};

  if (rawRoster !== undefined && !isPlainObject(rawRoster)) {
    issues.push('roster is not an object, so no badges resolve');
  } else if (isPlainObject(rawRoster)) {
    for (const [expertId, entry] of Object.entries(rawRoster)) {
      const parsed = rosterEntrySchema.safeParse(entry);

      if (!parsed.success) {
        issues.push(`dropped ${expertId} because its value is not true, false or a number`);

        continue;
      }

      roster[expertId] = parsed.data !== false;
    }
  }

  if (isCopyShape) {
    const unknownKeys = Object.keys(value).filter(
      (key) => !(COPY_KEYS as readonly string[]).includes(key)
    );

    if (unknownKeys.length > 0) {
      issues.push(`ignored unrecognized keys: ${unknownKeys.join(', ')}`);
    }
  }

  return { roster, label, emoji, issues };
}

/**
 * Whether an expert shows the badge. An entry that parsed to off is already absent, so a
 * missing key and an explicit `false` read the same here, and the wildcard covers anyone
 * the roster does not name. Callers still need to check the feature flag.
 */
export function isReferenceChampion(
  roster: ReferenceChampionRoster,
  expertId: string | number
): boolean {
  return Boolean(roster[String(expertId)] ?? roster[REFERENCE_CHAMPION_WILDCARD]);
}
